import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import {
  catchError,
  map,
  mergeMap,
  switchMap,
  withLatestFrom,
} from 'rxjs/operators';
import { bookingsActions } from '.';
import { ApiError } from '../../models/common/api-error';
import { filter, of, takeUntil } from 'rxjs';
import { BookingsService } from '../../services/bookings.service';
import { storeAuthKey, storeBookingsKey } from '../reducers';
import {
  BookingsDisplayMode,
  BookingsScope,
  BookingsState,
} from './bookings.state';
import {
  endOfDay,
  endOfMonth,
  endOfWeek,
  parseISO,
  startOfDay,
  startOfMonth,
  startOfWeek,
} from 'date-fns';
import { DatetimeHelper } from '../../helpers/datetime.helper';
import { BlockType, BookingStatus } from '../../models/entities/booking';
import { AuthState } from '../auth/auth.state';
import { RoomsService } from '../../services/rooms.service';

@Injectable({
  providedIn: 'root',
})
export class BookingsEffects {
  constructor(
    private store$: Store,
    private actions$: Actions,
    private bookingsService: BookingsService,
    private roomsService: RoomsService,
  ) {}

  findAll$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        bookingsActions.findAll,
        bookingsActions.setDisplayMode,
        bookingsActions.setCalendarDate,
        bookingsActions.setSearch,
        bookingsActions.setBookingsFilter,
        bookingsActions.setPage,
        bookingsActions.setRoomsFilter,
        bookingsActions.setOrder,
        bookingsActions.setDatesRange,
      ),
      withLatestFrom(this.store$),
      mergeMap(([action, store]) => {
        const scope = action.scope;
        const bookingsState: BookingsState = store[storeBookingsKey];
        let from = null;
        let to = null;
        let page = null;
        let search = null;
        let status = null;
        let order_by = null;
        let roomIds = bookingsState[action.scope].roomsFilter.ids;
        const displayMode = bookingsState[scope].displayMode;

        // En mode Liste, on filtre par block_types et is_public (configurables par l'utilisateur)
        // En mode Week/Day, on affiche tout (pas de filtre block_types ni is_public)
        let block_types: BlockType[] | null = null;
        let is_public: boolean | null = null;

        switch (displayMode) {
          case BookingsDisplayMode.Month:
            from = DatetimeHelper.dateToApiFormat(
              startOfWeek(startOfMonth(bookingsState[scope].calendarDate), {
                weekStartsOn: 1,
              }),
              true,
            );
            to = DatetimeHelper.dateToApiFormat(
              endOfWeek(endOfMonth(bookingsState[scope].calendarDate), {
                weekStartsOn: 1,
              }),
              true,
            );
            break;

          case BookingsDisplayMode.Week:
            from = DatetimeHelper.dateToApiFormat(
              startOfWeek(bookingsState[scope].calendarDate, {
                weekStartsOn: 1,
              }),
              true,
            );
            to = DatetimeHelper.dateToApiFormat(
              endOfWeek(bookingsState[scope].calendarDate, {
                weekStartsOn: 1,
              }),
              true,
            );
            break;

          case BookingsDisplayMode.Day:
            from = DatetimeHelper.dateToApiFormat(
              startOfDay(bookingsState[scope].calendarDate),
              true,
            );
            to = DatetimeHelper.dateToApiFormat(
              endOfDay(bookingsState[scope].calendarDate),
              true,
            );
            break;

          case BookingsDisplayMode.List:
            page = bookingsState[scope].page;
            search = bookingsState[scope].search;
            from = bookingsState[scope].from
              ? DatetimeHelper.dateToApiFormat(
                  startOfDay(parseISO(bookingsState[scope].from)),
                  true,
                )
              : null;
            to = bookingsState[scope].to
              ? DatetimeHelper.dateToApiFormat(
                  endOfDay(parseISO(bookingsState[scope].to)),
                  true,
                )
              : null;
            order_by = bookingsState[scope].order;
            block_types = bookingsState[scope].bookingsFilter.blockTypes;
            // [true] → public, [false] → interne, null ou les deux → pas de filtre
            if (bookingsState[scope].bookingsFilter.isPublic?.length === 1) {
              is_public = bookingsState[scope].bookingsFilter.isPublic[0];
            }
            break;
        }
        let user_id = null;
        if (scope === BookingsScope.Personal) {
          const authState: AuthState = store[storeAuthKey];
          user_id = authState.user?.id;
        }
        const nextSearch$ = this.actions$.pipe(
          ofType(
            bookingsActions.findAll,
            bookingsActions.setDisplayMode,
            bookingsActions.setCalendarDate,
            bookingsActions.setSearch,
            bookingsActions.setBookingsFilter,
            bookingsActions.setPage,
            bookingsActions.setRoomsFilter,
            bookingsActions.setOrder,
            bookingsActions.setDatesRange,
          ),
          filter((action) => action.scope === scope),
        );

        status = bookingsState[scope].bookingsFilter.status;

        return this.bookingsService
          .findAll({
            page,
            from,
            to,
            search,
            status,
            user_id,
            room_ids: roomIds,
            order_by: order_by?.map((orderBy) => [
              orderBy.field,
              orderBy.order,
            ]),
            minimum_data: true,
            block_types,
            is_public,
            single_booking_per_date_group:
              displayMode === BookingsDisplayMode.List,
          })
          .pipe(
            takeUntil(nextSearch$),
            map((response) =>
              bookingsActions.findAllSuccess({
                bookings: response.data,
                total: response.total,
                scope,
              }),
            ),
            catchError((error: ApiError) =>
              of(bookingsActions.findAllError({ error: error.message, scope })),
            ),
          );
      }),
    ),
  );

  findAllPending$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        bookingsActions.findAllPending,
        bookingsActions.findAllPendingSilent,
      ),
      switchMap(() => {
        return this.bookingsService
          .findAll({
            page: 1,
            status: [BookingStatus.Pending],
            single_booking_per_group: true,
            block_types: [BlockType.Booking],
          })
          .pipe(
            map((response) =>
              bookingsActions.findAllPendingSuccess({
                bookings: response.data,
                total: response.total,
              }),
            ),
            catchError((error: ApiError) =>
              of(bookingsActions.findAllPendingError({ error: error.message })),
            ),
          );
      }),
    ),
  );
}
